import { AgreementType } from './Agreement.ts'
import type {
    DocumentWithAuthor,
    DocumentWithPublicity,
    DocumentWithSoftDelete,
    DocumentWithTimestamps,
    Timestamp
} from './firebase.ts'
import type { OrgId, UserId } from './ids.ts'
import type { JobOutcomeStage } from './JobRequest.ts'
import type { NotificationEmail } from './Notifications.ts'
import type { ConsentsData, OrgMeta } from './Org.ts'
import type { RelatedItems } from './RelatedItems.ts'
import { VisibilityType } from './visibility.ts'

export const JOBS_DEFAULT_VISIBILITY = VisibilityType.unlisted

export interface JobLanguage {
    name: string
    level: string
}

export const internshipWhos = ['student_ss', 'student_vs']

export const defaultLanguage = {
    name: '',
    level: 'C1'
}

export const jobAgreementTypes = [AgreementType.jobMediationContract, AgreementType.internshipMediationContract]

export enum StartType {
    DATE = 'datum',
    MONTH = 'mesiac',
    ASAP = 'ihned'
}

export enum JobType {
    fullTime = 'trvaly_pracovny_pomer',
    partTime = 'ciastocny_uvazok',
    internship = 'staz_pre_studenta',
    paidInternship = 'staz_platena', // deprecated
    unpaidInternship = 'staz_neplatena' // deprecated
}

/**
 * @default SATISFACTION
 */
export enum JobFeeType {
    /**
     * This job fee type is the previous model used before fee types were introduced as an option.
     * It is free of charge for publication and sets the job end date to a maximum of three months
     * from either the publication date or the date specified by the user.
     */
    SATISFACTION = 'satisfaction',
    /**
     * This is the new model for job fee types. It is a paid option for publication, with the job
     * ending one month after publication and no maximum duration. Publishing the job requires
     * using paid credits.
     */
    ADVERTISING = 'advertising'
}

export enum JobContentMode {
    STRUCTURED = 'structured',
    CUSTOM = 'custom'
}

export enum FormOfCollaborationType {
    permanent = 'trvaly_pracovny_pomer',
    agreement = 'dohoda',
    invoice = 'faktura'
}

export enum JobPlaceType {
    homeoffice = 'homeoffice',
    office = 'office',
    hybrid = 'hybrid'
}

export enum JobTimeType {
    flexible = 'flexibilny',
    static = 'pevny'
}

export enum LanguageLevelType {
    A1 = 'A1',
    A2 = 'A2',
    B1 = 'B1',
    B2 = 'B2',
    C1 = 'C1',
    C2 = 'C2'
}

export enum SkillLevelType {
    A1 = 'A1',
    A2 = 'A2',
    B1 = 'B1',
    B2 = 'B2',
    C1 = 'C1',
    C2 = 'C2'
}

export enum WhoType {
    studentSs = 'student_ss',
    studentVs = 'student_vs',
    cerstvyAbsolventVs = 'cerstvy_absolvent_vs',
    mladyProfesional = 'mlady_profesional', // deprecated
    profesional = 'profesional' // deprecated
}

export enum DegreeType {
    highSchoolDiploma = 'maturita',
    firstYearOfBachelorsDegree = '1_rocnk_bakalarskeho_studia',
    secondYearOfBachelorsDegree = '2_rocnk_bakalarskeho_studia',
    thirdYearOfBachelorsDegree = '3_rocnk_bakalarskeho_studia',
    fourthYearOfBachelorsDegree = '4_rocnk_bakalarskeho_studia',
    mastersDegree = 'magisterske_inzinierske_studium',
    phD = 'phd_studium'
}

export enum EducationType {
    humanitiesAndSocialSciences = 'humanitne-a-spolocenske-vedy',
    economicsAndLaw = 'ekonomicke-a-pravne-vedy',
    marketingCommunicationAndMedia = 'marketing-komunikacia-media',
    naturalSciencesMathematicsAndInformationTechnologies = 'prirodne-vedy-matematika-a-informatika',
    technicalSciencesAndEngineering = 'technicke-vedy',
    art = 'umenie',
    teachingAndEducationSciences = 'ucitelstvo-a-pedagogicke-vedy',
    medicineMedicalAndPharmaceuticalSciences = 'zdravotnictvo-zdravotnicke-a-farmaceuticke-vedy',
    agriculturalAndVeterinarySciences = 'polnohospodarske-a-veterinarske-vedy',
    services = 'sluzby',
    physicalEducationAndSportsSciences = 'telesna-vychova-a-sport',
    interMultidisciplinaryPrograms = 'medziodborove-programy'
}

export enum EducationDetailsType {
    // humanitiesAndSocialSciences
    culturalAnthropology = 'Kultúrna antropológia',
    archeology = 'Archeológia',
    sociologyAndSocialAnthropology = 'Sociológia a sociálna antropológia',
    ethnology = 'Etnológia',
    culturology = 'Kulturológia',
    aesthethics = 'Estetika',
    philosophy = 'Filozofia',
    history = 'História',
    religion = 'Religionistika',
    politicalScience = 'Politológia',
    translationAndInterpretation = 'Prekladateľstvo a tlmočníctvo',
    psychology = 'Psychológia',
    sociology = 'Sociológia',
    liberalArts = 'Liberálne štúdiá',
    // economicsAndLaw
    economics = 'Ekonómia',
    finance = 'Financie',
    banking = 'Bankovníctvo',
    entrepreneurship = 'Podnikanie',
    accounting = 'Účtovníctvo',
    management = 'Manažment',
    humanResourcesAndPersonnelManagement = 'Ľudské zdroje a personálny manažment',
    internationalRelations = 'Medzinárodné vzťahy',
    law = 'Právo',
    // marketingCommunicationAndMedia
    marketing = 'Marketing',
    communicationAndMedia = 'Mediálne a komunikačné štúdiá',
    journalism = 'Žurnalistika',
    // naturalSciencesMathematicsAndInformationTechnologies
    physics = 'Fyzika',
    astronomy = 'Astronómia',
    astrophysics = 'Astrofyzika',
    chemistry = 'Chémia',
    biochemistry = 'Biochémia',
    biology = 'Biológia',
    geology = 'Geológia',
    genetics = 'Genetika',
    environmentalSciences = 'Environmentálne vedy',
    botany = 'Botanika',
    anthropology = 'Antropológia',
    mathematics = 'Matematika',
    statistics = 'Štatistika',
    computerScience = 'Informatika',
    cybernetics = 'Kybernetika',
    artificialIntelligence = 'Umelá inteligencia',
    cognitiveScience = 'Kognitívna veda',
    // technicalSciencesAndEngineering
    architectureAndUrbanism = 'Architektúra a urbanizmus',
    spatialPlanning = 'Priestorové plánovanie',
    construction = 'Stavebníctvo',
    engineering = 'Strojárstvo',
    automation = 'Automatizácia',
    electricalEngineering = 'Elektrotechnika',
    energy = 'Energetika',
    biotechnology = 'Biotechnológie',
    chemicalEngineeringAndTechnologies = 'Chemické inžinierstvo a technológie',
    foodIndustry = 'Potravinárstvo',
    transportation = 'Doprava',
    // art
    visualArts = 'Výtvarné umenia (sochárstvo, maliarstvo, fotografia, dizajn)',
    performingArts = 'Múzické umenia (hudba, tanec, divadlo, film)',
    // teachingAndEducationSciences
    education = 'Pedagogika',
    specialEducation = 'Špeciálna pedagogika',
    andragogy = 'Andragogika',
    speechAndLanguageTherapy = 'Logopédia',
    // medicineMedicalAndPharmaceuticalSciences
    pharmacy = 'Farmácia',
    pharmacology = 'Farmakológia',
    medicine = 'Medicína',
    nursing = 'Ošetrovateľstvo',
    socialWork = 'Sociálna práca',
    publicHealth = 'Verejné zdravotníctvo',
    medicalSciences = 'Zdravotnícke vedy',
    dentistry = 'Zubné lekárstvo',
    // agriculturalAndVeterinarySciences
    agriculture = 'Poľnohospodárstvo',
    foodHygiene = 'Hygiena potravín',
    veterinaryMedicine = 'Veterinárne lekárstvo',
    // services
    tourism = 'Cestovný ruch',
    transportationService = 'Dopravné služby'
}

export enum ExpertiseType {
    administration = 'administrativa',
    analysisAndPolicyMaking = 'analyza-a-tvorba-politik',
    architecture = 'architektura',
    businessDevelopment = 'business-development',
    finance = 'financie',
    informationTechnology = 'informacne-technologie',
    innovationAndStrategy = 'inovacie-a-strategie',
    humanResources = 'ludske-zdroje',
    marketing = 'marketing',
    law = 'pravo',
    translationsAndInterpreting = 'preklady-a-tlmocnictvo',
    projectManagement = 'projektovy-manazment',
    statisticsAndAnalytics = 'statistika-a-analytika',
    technicalDirections = 'technicke-smery',
    artAndDesign = 'umenie-a-dizajn',
    scienceResearch = 'veda-vyskum',
    education = 'vzdelavanie'
}

export enum EndedHow {
    success = 'success',
    noSuccess = 'noSuccess',
    notDecided = 'notDecided'
}

export type JobId = string

export type JobWorkProfile = {
    /** Holland letters (R, I, A, S, E, C) of what the work is mostly about, at most two */
    focus?: string[]
    collaboration?: 'solo' | 'mixed' | 'team'
    change?: 'stable' | 'mixed' | 'changing'
    leadsPeople?: boolean
}

export type Job = DocumentWithTimestamps &
    DocumentWithSoftDelete &
    DocumentWithPublicity &
    DocumentWithAuthor & {
        id: JobId
        userId: UserId
        orgId: OrgId
        orgMeta: OrgMeta
        isDraft: boolean
        relatedItems?: RelatedItems
        additionalInfo?: string
        highlights?: string
        benefits?: string
        challenge?: string
        content?: string
        contentMode?: JobContentMode
        clarity?: string
        clients?: string
        creativity?: string
        consents?: ConsentsData
        cv?: boolean
        degree?: string
        desc?: string
        duration?: string[]
        education?: string
        educationDetails?: string
        expertise?: string[]
        externalLink?: string // URL to redirect after request form submission. Link could be set only if ORG is "verejný sektor". Link is not required
        formOfCollaboration?: FormOfCollaborationType[]
        jobPlace?: JobPlaceType[]
        jobLocation?: string[]
        jobTime?: JobTimeType[]
        moreAboutFlexibility?: string
        image?: string
        contentURL?: string
        contentURLType?: string
        languages?: JobLanguage[]
        location?: string
        manager?: string
        managerLinkedin?: string
        motivation?: boolean
        multitasking?: string
        premium?: boolean
        remote?: boolean
        salaryMax?: number
        salaryMin?: number
        salaryNote?: string
        salaryUnit?: string
        /** How the work on this position looks; matched against shared Sebapoznanie (jobFit.ts) */
        workProfile?: JobWorkProfile
        skills?: JobLanguage[]
        startDate?: Timestamp
        startType?: StartType
        endDate?: Timestamp
        endedHow?: EndedHow
        whereFound?: string
        teamwork?: string
        title?: string
        topics?: string[]
        type?: JobType[] // Deprecated: JobType is now replaced by JobType[]
        feeType?: JobFeeType
        featured?: boolean
        who?: string[] // for whom / who R V looking for
        topPositionRequest?: boolean
        slug?: string
        lang?: string
        notifiableMembers: NotificationEmail[]
        /*
         * Admin-side follow-up of the success fee for this posting, driven from
         * /admin/job-outcomes. One posting is one case to chase, so it lives here rather than
         * on an individual application.
         */
        outcomeStage?: JobOutcomeStage
        outcomeStageUpdatedAt?: Timestamp
        outcomeStageUpdatedById?: UserId
        outcomeNote?: string
        outcomeEmailSentAt?: Timestamp
        outcomeEmailSentById?: UserId
        outcomeEmailSubject?: string
        /**
         * If true, the job offer is allowed to be accessed via External API.
         */
        isSharingWithPartnersAllowed?: boolean
        /**
         * Job cannot be edited by GROWNi because it was created by External API.
         */
        isReadOnly?: boolean
    }

export type JobFormData = Partial<Job>
